import { Button, Space, Typography, Form, InputNumber, Input, Select, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTruckRampBox, faPlus, faX, faCircleQuestion } from '@fortawesome/pro-solid-svg-icons'
import { TrailersStepProps } from '../../types'
import { MAX_ENTITIES, OWNERSHIP_TYPE_OPTIONS } from '../../constants'
import { checkEquipmentRow } from '../../inputSanityModel'
import InputWarning from '../InputWarning'

const { Title, Paragraph } = Typography

export const TrailersStep = ({
  data,
  onCountChange,
  onTrailerUpdate,
  onTrailerAdd,
  onTrailerRemove, crossInputCard }: TrailersStepProps) => {
  return (
    <Space
      direction="vertical"
      size="large"
      style={{
        width: '100%',
        maxWidth: '1200px',
        padding: '40px 20px'
      }}
    >
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Space direction="horizontal" size="middle" style={{ marginBottom: '16px' }}>
          <FontAwesomeIcon icon={faTruckRampBox} style={{ fontSize: '24px', color: '#ff6b4a' }} />
          <Title level={3} style={{ margin: 0 }}>Trailers</Title>
        </Space>

        <Paragraph style={{ color: '#666', marginBottom: '32px' }}>
          Add trailers and calculate depreciation costs.
        </Paragraph>

        {crossInputCard}

        <Form layout="vertical">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form.Item
              label="How many trailers do you have?"
              required
              style={{ marginBottom: 0 }}
              help="Total number of trailers in your fleet"
            >
              <InputNumber
                value={data.trailerCount}
                onChange={(value) => onCountChange(value || 0)}
                min={0}
                max={MAX_ENTITIES}
                style={{ width: '100%' }}
                size="large"
              />
            </Form.Item>

            {data.trailers.map((trailer, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                  padding: '24px',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <Title level={5} style={{ margin: 0 }}>Trailer #{index + 1}</Title>
                  <Button
                    type="text"
                    icon={<FontAwesomeIcon icon={faX} />}
                    onClick={() => onTrailerRemove(index)}
                    style={{ color: '#999' }}
                  />
                </div>

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Space direction="horizontal" size="large" style={{ width: '100%', alignItems: 'flex-start' }}>
                    <Form.Item
                      label="Trailer Name"
                      required
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input
                        value={trailer.name}
                        onChange={(e) => onTrailerUpdate(index, 'name', e.target.value)}
                        size="large"
                        placeholder="Trailer 1"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Ownership Type"
                      required
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Select
                        value={trailer.ownershipType}
                        onChange={(value) => onTrailerUpdate(index, 'ownershipType', value)}
                        size="large"
                        options={OWNERSHIP_TYPE_OPTIONS}
                      />
                    </Form.Item>
                  </Space>

                  {trailer.ownershipType === 'Own' ? (
                    <>
                      <Space direction="horizontal" size="large" style={{ width: '100%', alignItems: 'flex-start' }}>
                        <Form.Item
                          label={
                            <span>
                              Replacement Cost{' '}
                              <Tooltip title="The cost to replace this trailer with a similar vehicle">
                                <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#999', fontSize: '14px' }} />
                              </Tooltip>
                            </span>
                          }
                          required
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            prefix="$"
                            value={trailer.replacementCost}
                            onChange={(value) => onTrailerUpdate(index, 'replacementCost', value)}
                            min={0}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="10,000"
                          />
                        </Form.Item>

                        <Form.Item
                          label={
                            <span>
                              Avg Useful Life{' '}
                              <Tooltip title="Average number of years this trailer will be in service">
                                <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#999', fontSize: '14px' }} />
                              </Tooltip>
                            </span>
                          }
                          required
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            value={trailer.avgUsefulLife}
                            onChange={(value) => onTrailerUpdate(index, 'avgUsefulLife', value)}
                            min={0}
                            addonAfter="Years"
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="10"
                          />
                        </Form.Item>

                        <Form.Item
                          label={
                            <span>
                              Sell Price{' '}
                              <Tooltip title="Expected resale value at the end of its useful life">
                                <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#999', fontSize: '14px' }} />
                              </Tooltip>
                            </span>
                          }
                          required
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            prefix="$"
                            value={trailer.sellPrice}
                            onChange={(value) => onTrailerUpdate(index, 'sellPrice', value)}
                            min={0}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="3,500"
                          />
                        </Form.Item>
                      </Space>

                      <InputWarning warning={checkEquipmentRow(trailer)} />
                    </>
                  ) : (
                    <Form.Item
                      label="Monthly Payment"
                      required
                      style={{ flex: 1, marginBottom: 0, maxWidth: '50%' }}
                    >
                      <InputNumber
                        prefix="$"
                        value={trailer.monthlyPayment}
                        onChange={(value) => onTrailerUpdate(index, 'monthlyPayment', value)}
                        min={0}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                        style={{ width: '100%' }}
                        size="large"
                        placeholder="1,000"
                      />
                    </Form.Item>
                  )}
                </Space>
              </div>
            ))}

            <Button
              size="large"
              onClick={onTrailerAdd}
              icon={<FontAwesomeIcon icon={faPlus} />}
              style={{ width: '100%' }}
              disabled={data.trailers.length >= MAX_ENTITIES}
            >
              Add Another Trailer
            </Button>
          </Space>
        </Form>
      </div>
    </Space>
  )
}
